import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { changePassword, getMe, clearToken, formatApiError } from "@/lib/api";
import { ADMIN_BASE, ADMIN_LOGIN } from "@/lib/adminPath";

const inputCls =
  "w-full rounded-sm border border-espresso-border bg-espresso-2 px-4 py-3.5 text-sm text-ivory outline-none transition-colors duration-200 placeholder:text-ivory/30 focus:border-primary focus:ring-1 focus:ring-primary";

const regles = [
  { id: "longueur", label: "Au moins 10 caractères", ok: (m) => m.length >= 10 },
  { id: "majuscule", label: "Une majuscule", ok: (m) => /[A-Z]/.test(m) },
  { id: "minuscule", label: "Une minuscule", ok: (m) => /[a-z]/.test(m) },
  { id: "chiffre", label: "Un chiffre", ok: (m) => /\d/.test(m) },
  { id: "special", label: "Un caractère spécial", ok: (m) => /[^A-Za-z0-9]/.test(m) },
];

export default function AdminChangePassword() {
  const [actuel, setActuel] = useState("");
  const [nouveau, setNouveau] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [erreur, setErreur] = useState("");
  const [chargement, setChargement] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getMe().catch(() => {
      clearToken();
      navigate(ADMIN_LOGIN);
    });
  }, [navigate]);

  const fort = regles.every((r) => r.ok(nouveau));

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur("");
    if (nouveau !== confirmation) {
      setErreur("La confirmation ne correspond pas au nouveau mot de passe.");
      return;
    }
    if (!fort) {
      setErreur("Le nouveau mot de passe ne respecte pas toutes les règles de sécurité.");
      return;
    }
    setChargement(true);
    try {
      await changePassword(actuel, nouveau);
      toast.success("Mot de passe modifié. Bienvenue dans votre espace admin !");
      navigate(ADMIN_BASE);
    } catch (err) {
      setErreur(formatApiError(err.response?.data?.detail));
    } finally {
      setChargement(false);
    }
  };

  return (
    <section className="flex min-h-[85vh] items-center justify-center bg-espresso px-6 py-20" data-testid="admin-change-password-page">
      <div className="w-full max-w-md border border-espresso-border bg-espresso-2 p-10">
        <div className="flex items-center gap-4">
          <img src="/logo-hora-dark.png" alt="Fondation HORA" className="h-12 w-auto" data-testid="admin-change-password-logo" />
          <span className="border-l border-espresso-border pl-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
            Espace admin
          </span>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-ivory/60" data-testid="admin-change-password-intro">
          Pour sécuriser votre compte, vous devez remplacer le mot de passe temporaire par un mot de passe fort avant d'accéder au tableau de bord.
        </p>

        <form onSubmit={soumettre} className="mt-8 space-y-5" data-testid="admin-change-password-form">
          <div>
            <label htmlFor="mdp-actuel" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">Mot de passe actuel</label>
            <input id="mdp-actuel" type="password" required value={actuel} onChange={(e) => setActuel(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="admin-change-password-current-input" />
          </div>
          <div>
            <label htmlFor="mdp-nouveau" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">Nouveau mot de passe</label>
            <input id="mdp-nouveau" type="password" required value={nouveau} onChange={(e) => setNouveau(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="admin-change-password-new-input" />
            <ul className="mt-3 space-y-1" data-testid="admin-change-password-rules">
              {regles.map((r) => (
                <li key={r.id} className={`text-xs ${r.ok(nouveau) ? "text-green-400" : "text-ivory/40"}`} data-testid={`admin-change-password-rule-${r.id}`}>
                  {r.ok(nouveau) ? "✓" : "•"} {r.label}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <label htmlFor="mdp-confirmation" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">Confirmer le nouveau mot de passe</label>
            <input id="mdp-confirmation" type="password" required value={confirmation} onChange={(e) => setConfirmation(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="admin-change-password-confirm-input" />
          </div>
          {erreur && (
            <p className="border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-red-300" data-testid="admin-change-password-error">{erreur}</p>
          )}
          <button
            type="submit"
            disabled={chargement}
            data-testid="admin-change-password-submit-button"
            className="flex w-full items-center justify-center gap-3 rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,opacity] duration-200 hover:bg-[hsl(22,83%,40%)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <KeyRound size={16} />
            {chargement ? "Enregistrement…" : "Enregistrer mon nouveau mot de passe"}
          </button>
        </form>
      </div>
    </section>
  );
}
